import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Button, Container, IconButton, Box,
} from '@mui/material';
import Badge from '@mui/material/Badge';
import NotificationIcon from '@mui/icons-material/Notifications';
import { Person, Settings } from '@mui/icons-material';
import moment from 'moment';
import csrmData from '../../data/csrm.json';
import Select from './components/Select/Select';
import Charts from './components/Charts/Charts';
import DateRangePicker from './components/DateRangePicker/DateRangePicker';
import TimeRangePicker from './components/TimeRangePicker/TimeRangePicker';
import {
  PlaybackBar,
  PlaybackSpeeds,
} from './components/PlaybackBar/PlaybackBar';
import SettingsPanel from './components/Panels/SettingsPanel';
import AlertsPanel from './components/Panels/AlertsPanel';
import SubjectModal from './components/SubjectModal/SubjectModal';
import AlertHighlightPanel from './components/Panels/AlertHighlightPanel';

import './components/common.scss';
import './CsrmPage.scss';

function combineDateAndTime(date = new Date(), time = new Date()) {
  const dateFormat = 'YYYY-MM-DD';
  const timeFormat = 'HH:mm:ss.SSS';
  const dateStr = moment(date).format(dateFormat);
  const timeStr = moment(time).format(timeFormat);
  return moment(`${dateStr} ${timeStr}`, `${dateFormat} ${timeFormat}`);
}

function processRawData(values = [], timestamps = []) {
  const dataSet = [];
  values.forEach((val, idx) => {
    dataSet.push({
      value: val,
      timestamp: new Date(timestamps[idx]).getTime(),
    });
  });
  return dataSet;
}

function alertsLabel(count) {
  if (count === 0) {
    return 'no notifications';
  }
  if (count > 99) {
    return 'more than 99 notifications';
  }
  return `${count} notifications`;
}

function parseCsrmDataSets(data = {}) {
  return {
    meas_mr: {
      name: 'Meas Met Rate',
      unit: 'Unknown Unit',
      color: '#D93E3E',
      data: processRawData(data.meas_mr, data.meas_mr_ts),
    },
    pred_mr: {
      name: 'Pico Met Rate',
      unit: 'Unknown Unit',
      color: '#0979d0',
      data: processRawData(data.pred_mr, data.pred_mr_ts),
    },
  };
}

function sortAlerts(alerts) {
  return [...alerts].sort((a, b) => (moment(a.timestamp).isAfter(b.timestamp) ? -1 : 1));
}

export default function CsrmPage() {
  const { state } = useLocation() || {};

  const [subjectId, setSubjectId] = useState(state?.subjectId || 'Unknown');
  const [dateRange, setDateRange] = useState(
    state?.dateRange || [new Date(), new Date()],
  );
  const [startTime, setStartTime] = useState(state?.startTime || new Date());
  const [endTime, setEndTime] = useState(state?.endTime || new Date());
  const [showModal, setShowModal] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showAlerts, setShowAlerts] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(state?.playbackSpeed || 1);
  const [playbackPosition, setPlaybackPosition] = useState(
    combineDateAndTime(dateRange[0], startTime).valueOf()
      || new Date().valueOf(),
  );

  const [pulseAlertId, setPulseAlertId] = useState(null);
  // TODO: retrieve alerts from api
  const [alerts, setAlerts] = useState(
    sortAlerts([
      {
        id: 0,
        text: 'This is a sample alert',
        timestamp: moment('2022-06-01 1:44:12 PM', 'YYYY-MM-DD h:mm:ss A'),
      },
      {
        id: 1,
        text:
          'This is another sample alert with much longer text to '
          + 'emphasise how complex and important the alert is and how a user '
          + 'should be able to see the entire text when skimming through their alerts.',
        timestamp: moment('2022-05-01 4:20:55 AM', 'YYYY-MM-DD h:mm:ss A'),
      },
      {
        id: 2,
        text: 'Tiny alert',
        timestamp: moment('2022-04-23 11:59:59 AM', 'YYYY-MM-DD h:mm:ss A'),
      },
      {
        id: 3,
        text:
          'An alert of moderate but respectible size that might break into more than one or two lines. Puctuation!?&#$%!@#$&*^)(*&^$#@!',
        timestamp: moment('2022-04-23 12:00:00 PM', 'YYYY-MM-DD h:mm:ss A'),
      },
      {
        id: 4,
        text: 'An out of order alert',
        timestamp: moment('2022-05-10 6:00:00 PM', 'YYYY-MM-DD h:mm:ss A'),
      },
      ...[5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15].map((id) => ({
        id,
        text: 'Filler alert meant to force the user to scroll',
        timestamp: moment(`2022-02-${id} 3:00:00 PM`, 'YYYY-MM-DD h:mm:ss A'),
      })),
    ]),
  );
  const [alertHighlight, setAlertHighlight] = useState(
    alerts.length ? alerts[0] : null,
  );
  const [showAlertHighlight, setShowAlertHighlight] = useState(
    !!alertHighlight,
  );

  useEffect(() => {
    if (alerts.length && alertHighlight && alertHighlight.id !== alerts[0].id) {
      setAlertHighlight(alerts[0]);
    }

    if (alertHighlight && !showAlertHighlight) {
      setShowAlertHighlight(true);
    }

    if (!alerts.length && showAlertHighlight) {
      setShowAlertHighlight(false);
    }
  }, [alerts]);

  const dataSets = parseCsrmDataSets(csrmData);

  // TODO: data date parsing for testing only. Should be replaced by actual playback logic once ready
  const dataDates = dataSets.meas_mr.data.map((d) => d.timestamp);
  const minDataDate = moment(Math.min(...dataDates));
  const maxDataDate = moment(Math.max(...dataDates));
  const maxMeasuredDate = maxDataDate.subtract(
    Math.round((maxDataDate.valueOf() - minDataDate.valueOf()) / 2),
    'milliseconds',
  );
  // end TODO

  const chartMargins = {
    top: 5,
    left: 5,
    right: 5,
    bottom: 15,
  };

  const panelContainer = useRef(null);
  const [pageHeight, setPageHeight] = useState(0);

  const bannerContainer = useRef(null);
  const container = useRef(null);
  useEffect(() => {
    setPageHeight(container?.current?.clientHeight);
  });

  const [selectedDataTypes, setSelectedDataTypes] = useState([]);
  const dataTypeOptions = Object.keys(dataSets).map((dsKey) => ({
    name: dataSets[dsKey].name,
    value: dsKey,
  }));
  dataTypeOptions.sort((a, b) => a.name < b.name);

  const startPlayback = () => {
    // TODO: implement
  };

  const stopPlayback = () => {
    // TODO: implement
  };

  const dismissAlert = (id) => {
    // TODO: remove alert from user's account
    const newAlerts = sortAlerts(alerts.filter((a) => a.id !== id));
    setAlerts(newAlerts);
    if (newAlerts.length) {
      setAlertHighlight(alerts[0]);
    }
  };

  const dismissAlertHighlight = (id) => {
    // TODO: remove alert from user's account
    dismissAlert(id);
  };

  const clearAllAlerts = () => {
    setAlerts([]);
    setShowAlerts(false);
  };

  return (
    <>
      <div className="csrm-container" ref={container}>
        <div className="alert-highlight-container" ref={bannerContainer}>
          {showAlertHighlight ? (
            <AlertHighlightPanel
              alert={alertHighlight}
              showAllAlerts={() => {
                setPulseAlertId(alertHighlight.id);
                setShowAlerts(true);
              }}
              close={() => setShowAlertHighlight(false)}
              container={bannerContainer}
              dismissAlert={dismissAlertHighlight}
            />
          ) : (
            ''
          )}
        </div>
        <div className="header-container">
          <h1>Crew State and Risk Model</h1>

          <IconButton
            aria-label={alertsLabel(alerts.length)}
            title={alertsLabel(alerts.length)}
            onClick={() => {
              setShowSettings(false);
              setShowAlerts(!showAlerts);
            }}
          >
            <Badge className="alert-badge" badgeContent={alerts.length}>
              <NotificationIcon />
            </Badge>
          </IconButton>
          {showAlerts ? (
            <AlertsPanel
              height={pageHeight}
              close={() => setShowAlerts(false)}
              container={panelContainer}
              alerts={alerts}
              dismissAlert={dismissAlert}
              clearAll={clearAllAlerts}
              pulseAlertId={pulseAlertId}
              clearPulseAlertId={() => setPulseAlertId(null)}
            />
          ) : (
            ''
          )}
        </div>
        <Container className="csrm-controls">
          <Select
            onSelect={setSelectedDataTypes}
            selectedDataTypes={selectedDataTypes}
            dataTypeOptions={dataTypeOptions}
          />

          <DateRangePicker
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            useToSeperator
            showOutline
          />

          <div className="separator" />

          <TimeRangePicker
            startTime={startTime}
            onStartTimeChange={setStartTime}
            endTime={endTime}
            onEndTimeChange={setEndTime}
            useToSeperator
            showOutline
          />

          <div className="separator" />

          <Button
            sx={{ color: '#424242' }}
            size="large"
            variant="text"
            startIcon={<Person />}
            disabled={showModal}
            onClick={() => setShowModal(true)}
          >
            <span>{`H3PO ID ${subjectId}`}</span>
          </Button>

          <div className="responsive-fill" />

          <Button
            sx={{ color: '#424242' }}
            size="large"
            variant="text"
            startIcon={<Settings />}
            onClick={() => {
              setShowAlerts(false);
              setShowSettings(!showSettings);
            }}
          >
            <span>Chart Options</span>
          </Button>
          {showSettings ? (
            <SettingsPanel
              close={() => setShowSettings(false)}
              container={panelContainer}
              height={pageHeight}
            />
          ) : (
            ''
          )}
        </Container>
        <div className="chart-area">
          <div className="card">
            <Charts
              className="csrm-charts"
              types={selectedDataTypes}
              chartMargins={chartMargins}
              dataSets={dataSets}
              maxMeasuredDate={maxMeasuredDate}
              timeRange={[
                combineDateAndTime(dateRange[0], startTime).valueOf(),
                combineDateAndTime(dateRange[1], endTime).valueOf(),
              ]}
            />
          </div>
        </div>

        <PlaybackBar
          disabled={!selectedDataTypes?.length}
          playbackSpeeds={PlaybackSpeeds}
          playbackSpeed={playbackSpeed}
          setPlaybackSpeed={setPlaybackSpeed}
          playPlayback={startPlayback}
          pausePlayback={stopPlayback}
          playbackPosition={playbackPosition}
          setPlaybackPosition={(increment) => setPlaybackPosition(increment + playbackPosition)}
          minPlaybackPosition={combineDateAndTime(
            dateRange[0],
            startTime,
          ).valueOf()}
          maxPlaybackPosition={combineDateAndTime(
            dateRange[1],
            endTime,
          ).valueOf()}
        />

        <SubjectModal
          showModal={showModal}
          setShowModal={setShowModal}
          subjectId={subjectId}
          setSubjectId={setSubjectId}
          close={() => setShowModal(false)}
        />
      </div>

      <Box ref={panelContainer} />
    </>
  );
}
